import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Badge,
  Box,
  Button,
  Icon,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { testIds } from '../../../../tests/constants';
import {
  selectAllBulkAction,
  setAlteastOneIsSelected,
  setBulkActionAlert,
  setBulkActionBySelectAll,
  setCertsTabs,
  setCheckForBulkAction,
  setIndeterminateForBulkAction,
  setSidePanelVisible,
  setTabHightLightKey,
} from '../../../redux/certdetails/action';
import { SET_IS_REASSIGN_POPOVER_MSG } from '../../../redux/certdetails/types';
import {
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';

import {
  TAB_STATUS_ALL,
  TAB_STATUS_CONS,
  TAB_STATUS_DONE,
  TAB_STATUS_TODO,
} from './constant';
import { invertedMessages } from './messages';

import './CertsControlTabs.css';

function CertsControlTabs({ addTabItemTestIds }) {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [tempTabs, setTempTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('');

  const {
    bulkAction: {
      isAtleastOneSelected,
      isChecked: isBulkChecked,
      isIndeterminate: isBulkIndeterminate,
    },
    global: { certKey, isTouchEnabled, mediaQueryValue },
    reviewAndSubmit: { primaryCertifier },
    tabs: { highlightTabName, tabData },
    lineActions: { reassignConfigMap },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);

  const { isSecondaryCertifierLoggedIn } = reassignConfigMap;

  const {
    CONTROL_TABS_TODO,
    CONTROL_TABS_DONE,
    CONTROL_TABS_CONS,
    CONTROL_TABS_ALL,
  } = testIds;

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  useEffect(() => {
    dispatch(setSidePanelVisible(false));
  }, [activeTab]);

  useEffect(() => {
    if (tabData) {
      const newTabs = tabData.map((tab) => ({
        ...tab,
        label: tab.label,
        isActive: tab.key === activeTab,
        isHighlighted: highlightTabName && tab.key === highlightTabName,
      }));

      setTempTabs(newTabs);
      setActiveTab(tabData.find((tab) => tab.isActive)?.key || tabData[0]?.key);
    }
  }, [tabData, highlightTabName, primaryCertifier]);

  // Reset highlightTabName after animation plays
  useEffect(() => {
    if (highlightTabName) {
      const timer = setTimeout(() => {
        dispatch(setTabHightLightKey(null));
      }, 1550);

      return () => clearTimeout(timer);
    }

    return () => {};
  }, [highlightTabName]);

  const handleChange = (key) => {
    // reset on tab changes
    dispatch({ type: SET_IS_REASSIGN_POPOVER_MSG, payload: false });

    if (isBulkChecked || isBulkIndeterminate) {
      dispatch(setBulkActionBySelectAll(true));

      if (
        (tabData.find((tab) => tab.isActive)?.key || tabData[0]?.key) ===
        activeTab
      ) {
        dispatch(selectAllBulkAction(certKey, true))
          .then(() => {
            dispatch(setCheckForBulkAction(false));
            dispatch(setIndeterminateForBulkAction(false));
            dispatch(setAlteastOneIsSelected(false));
          })
          .catch((error) => {
            dispatch(setBulkActionAlert(true, error.message));
          });
      }
    }

    setActiveTab(key);
    dispatch(
      setCertsTabs(
        tabData.map((tab) => ({
          ...tab,
          isActive: tab.key === key,
          isHighlighted: false,
        }))
      )
    );

    if (
      !isBulkChecked &&
      !isAtleastOneSelected &&
      (tabData.find((tab) => tab.isActive)?.key || tabData[0]?.key) ===
        activeTab
    ) {
      dispatch(setCheckForBulkAction(false));
      dispatch(setIndeterminateForBulkAction(false));
    }
  };

  const getTooltipText = (key) => {
    switch (key) {
      case TAB_STATUS_TODO:
        return (
          messages.CERTS_TODO_TOOLTIP ||
          localize(intl, getMessageKey('CERTS_TODO_TOOLTIP'))
        );
      case TAB_STATUS_DONE:
        return (
          messages.CERTS_DONE_TOOLTIP ||
          localize(intl, getMessageKey('CERTS_DONE_TOOLTIP'))
        );
      case TAB_STATUS_ALL:
        return (
          messages.CERTS_ALL_TOOLTIP ||
          localize(intl, getMessageKey('CERTS_ALL_TOOLTIP'))
        );
      case TAB_STATUS_CONS:
        return (
          messages.CERTS_CONSULTED_TOOLTIP ||
          localize(intl, getMessageKey('CERTS_CONSULTED_TOOLTIP'))
        );
      default:
        return 'Default Tooltip Text';
    }
  };

  const handlePopoverClick = () => {
    if (tabData) {
      const newTabs = tabData.map((tab) => ({
        ...tab,
        isHighlighted: false,
        showSaviPopupTab: false,
      }));

      dispatch(setCertsTabs(newTabs));
    }
  };

  const setTestIds = (tabKey) => {
    if (!addTabItemTestIds) return null;

    switch (tabKey) {
      case TAB_STATUS_TODO:
        return CONTROL_TABS_TODO;
      case TAB_STATUS_CONS:
        return CONTROL_TABS_CONS;
      case TAB_STATUS_DONE:
        return CONTROL_TABS_DONE;
      case TAB_STATUS_ALL:
        return CONTROL_TABS_ALL;
      default:
        return null;
    }
  };

  // TODO: Update these generic class names to suitcss utility conventions
  const getSaviPopoverContent = () => (
    <Box className='Savi-Companion-Popover-Wrapper'>
      <Box className='DisplayFlex'>
        <Icon kind='SaviCompanionGradient' size='medium' />
        <Typography kind='h3'>
          {messages.CERTS_SMART_REVIEW_APPLIED ||
            localize(intl, getMessageKey('CERTS_SMART_REVIEW_APPLIED'))}
        </Typography>
      </Box>
      <Typography kind='body1' className='DescriptionText'>
        {messages.CERTS_SMART_REVIEW_DESCRIPTION ||
          localize(intl, getMessageKey('CERTS_SMART_REVIEW_DESCRIPTION'))}
      </Typography>
      <Box className='flexWrapper'>
        <Button
          type='button'
          kind='filled'
          size='medium'
          onClick={() => handlePopoverClick(false)}>
          {messages.CERTS_REJECT_SECONDARY_BTN ||
            localize(intl, getMessageKey('CERTS_REJECT_SECONDARY_BTN'))}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box tag='div' className='CertsControlTabs-contextWrapper'>
      <TabGroup
        value={activeTab}
        onChange={handleChange}
        classNames={{
          fullWidthBorderClassName:
            'CertsControlsGroup-tabsAndControls--fullWidthBorder',
          pointerLeftClassName:
            'CertsControlsGroup-tabsAndControls--pointerLeft',
          pointerRightClassName:
            'CertsControlsGroup-tabsAndControls--pointerRight',
        }}
        isHorizontalScroll={isMobileXlargeWidth}>
        {tempTabs
          ?.filter((tab) => {
            if (
              tab.key === TAB_STATUS_CONS &&
              tab.count !== 0 &&
              isSecondaryCertifierLoggedIn
            ) {
              return true;
            }

            return (
              tab.key !== TAB_STATUS_CONS ||
              (primaryCertifier && tab.count !== 0)
            );
          })
          ?.map((tab) => {
            const tabItemClass = classnames(
              tab?.isHighlighted && 'CertsControlTabs-tabItem--isHighlighted'
            );

            return (
              <TabItem
                key={tab.key}
                label={l(tab.label)}
                value={tab.key}
                BadgeComp={
                  <Badge
                    badgeStyle={activeTab === tab.key ? 'filled' : 'outlined'}
                    number={tab.count}
                    color='secondary'
                    kind='number'
                    size='medium'
                    className='CertsControlTabs-tabItem--badge'
                    classNames={{
                      numberClassName: 'CertsControlTabs-tabItem--badgeNumber',
                    }}
                    // TODO: @Jacob, @Mike, CR-17904, Find another way to select? Or condition this
                    dataTestId={
                      addTabItemTestIds ? `${setTestIds(tab.key)}-badge` : null
                    }
                  />
                }
                tooltipProps={
                  isTouchEnabled
                    ? {}
                    : {
                        text: getTooltipText(tab.key),
                        maxWidth: '18rem',
                        isPortalEnabled: true,
                      }
                }
                popoverProps={{
                  isOpen: tab.showSaviPopupTab,
                  setIsOpen: handlePopoverClick,
                  isPortalEnabled: true,
                }}
                className={tabItemClass}
                dataTestId={setTestIds(tab.key)}
                PopoverContent={
                  // Reason for fragmanet "Warning: Signature declarations can only be used in TypeScript files without the fragment"
                  tab.showSaviPopupTab ? <>{getSaviPopoverContent()}</> : null
                }
              />
            );
          })}
      </TabGroup>
    </Box>
  );
}

CertsControlTabs.propTypes = { addTabItemTestIds: PropTypes.bool };

CertsControlTabs.defaultProps = { addTabItemTestIds: false };

export default CertsControlTabs;
